<template>
  <div>
    <LoadingOverlay v-if="loading" message="Just crunching some numbers..." />
    <div>
      <div id="view-sub-nav" class="justify-space-between">
        <div class="d-flex align-center">
          <v-switch color="primary" :label="regionLabel" @update:model-value="toggleRegion" hide-details></v-switch>
          <v-switch
            color="primary"
            :label="invoicedSummary ? 'Invoiced (Xero)' : 'Ordered'"
            @update:model-value="toggleMode"
            hide-details
            class="ml-6"
          ></v-switch>
          <DateRangePicker default="empty" class="ml-6" @change="dateRangeChanged"></DateRangePicker>
        </div>
        <div class="d-flex align-center">
          <v-btn
            variant="outlined"
            color="primary"
            @click="downloadCSV"
            :disabled="tableItems.length === 0"
            class="ml-4"
          >
            <v-icon start>mdi-download</v-icon>
            Download CSV
          </v-btn>
        </div>
      </div>
      <div>
        <v-container fluid>
          <p v-if="invoicedSummary" class="text-caption text-grey mb-2">
            Live from Xero, keyed on invoice date (dispatch/delivery date) — includes samples, FOC, recuts{{
              usSummary ? '' : ' and EU'
            }}, plus anything raised directly in Xero.
            <template v-if="usSummary">
              Tax is the TaxJar sales tax line on each invoice — Xero itself reports no tax on the US org.
            </template>
          </p>
          <v-row>
            <v-col v-for="card in summaryCards" :key="card.title" :cols="summaryCardCols">
              <v-card>
                <v-card-title>{{ card.title }}</v-card-title>
                <v-card-text>
                  <div v-for="(line, lineIndex) in card.lines" :key="lineIndex">
                    <span v-if="line.label" class="text-grey">{{ line.label }}: </span>
                    <span>{{ line.value }}</span>
                    <small v-if="line.note"> {{ line.note }}</small>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </div>
      <v-data-table :items="tableItems" :headers="headers">
        <!-- Ordered mode -->
        <template #item.orderCreated="{ item }">
          {{ $utils.convertDate(item.orderCreated) }}
        </template>
        <template #item.orderNumber="{ item }">
          {{ item.orderNumber || '-' }}
        </template>
        <template #item.orderTotal="{ item }">
          {{ usSummary ? '$' : '£' }}{{ $numeral(item.orderTotal).format('0,0.00') }}
        </template>

        <!-- Invoiced mode -->
        <template #item.reference="{ item }">
          {{ item.reference || '-' }}
        </template>
        <template #item.date="{ item }">
          {{ item.date ? $moment(item.date, 'YYYY-MM-DD').format('DD/MM/YYYY') : '-' }}
        </template>
        <template #item.source="{ item }">
          <v-chip size="x-small" :color="item.source === 'Not in MA' ? 'warning' : undefined">{{ item.source }}</v-chip>
        </template>
        <template #item.subTotal="{ item }">
          {{ $numeral(item.subTotal).format('0,0.00') }}
        </template>
        <template #item.totalTax="{ item }">
          {{ $numeral(item.totalTax).format('0,0.00') }}
        </template>
        <template #item.total="{ item }">
          {{ $numeral(item.total).format('0,0.00') }}
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script type="text/javascript">
import DateRangePicker from '@/components/ui/DateRangePicker'
import LoadingOverlay from '@/components/ui/LoadingOverlay'
import { db, functions } from '@/js/db'
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'

const CURRENCY_SYMBOLS = { GBP: '£', USD: '$', EUR: '€' }

const ORDERED_HEADERS = [
  { title: 'Name', sortable: false, key: 'name' },
  { title: 'Customer Email', sortable: false, key: 'customerEmail' },
  { title: 'Order Created', sortable: false, key: 'orderCreated' },
  { title: 'Order #', sortable: false, key: 'orderNumber' },
  { title: 'Total', sortable: false, key: 'orderTotal' }
]

const INVOICED_HEADERS = [
  { title: 'Invoice #', sortable: false, key: 'invoiceNumber' },
  { title: 'Order #', sortable: false, key: 'reference' },
  { title: 'Contact', sortable: false, key: 'contactName' },
  { title: 'Customer Email', sortable: false, key: 'customerEmail' },
  { title: 'Invoice Date', sortable: false, key: 'date' },
  { title: 'Source', sortable: false, key: 'source' },
  { title: 'Address', sortable: false, key: 'customerAddress' },
  { title: 'City', sortable: false, key: 'customerCity' },
  { title: 'State', sortable: false, key: 'customerState' },
  { title: 'Postcode', sortable: false, key: 'customerPostcode' },
  { title: 'Country', sortable: false, key: 'customerCountry' },
  { title: 'Currency', sortable: false, key: 'currencyCode' },
  { title: 'Net', sortable: false, key: 'subTotal', align: 'end' },
  { title: 'Tax', sortable: false, key: 'totalTax', align: 'end' },
  { title: 'Total', sortable: false, key: 'total', align: 'end' },
  { title: 'Status', sortable: false, key: 'status' }
]

export default {
  name: 'OrderSummary',
  components: { DateRangePicker, LoadingOverlay },
  data() {
    return {
      usSummary: false,
      invoicedSummary: false,
      activeDateRange: null,
      usSummaryOrderSum: null,
      ukSummaryOrderSum: null,
      orders: [],
      invoices: [],
      creditNotes: [],
      orderMetaByNumber: {},
      loading: false
    }
  },
  computed: {
    regionLabel() {
      if (this.invoicedSummary) {
        return this.usSummary ? 'US (Plykea Inc)' : 'UK + EU (Plykea Ltd)'
      }
      return this.usSummary ? 'US Summary' : 'UK Summary'
    },
    headers() {
      return this.invoicedSummary ? INVOICED_HEADERS : ORDERED_HEADERS
    },
    tableItems() {
      return this.invoicedSummary ? this.invoices : this.orders
    },
    totalsByCurrency() {
      const totals = {}
      const bucket = (code) => {
        const key = code || 'UNKNOWN'
        if (!totals[key]) {
          totals[key] = { net: 0, tax: 0, gross: 0, creditNet: 0, creditGross: 0, creditCount: 0 }
        }
        return totals[key]
      }

      this.invoices.forEach((i) => {
        const t = bucket(i.currencyCode)
        t.net += i.subTotal
        t.tax += i.totalTax
        t.gross += i.total
      })
      this.creditNotes.forEach((c) => {
        const t = bucket(c.currencyCode)
        t.creditNet += c.subTotal
        t.creditGross += c.total
        t.creditCount++
      })

      return totals
    },
    summaryCards() {
      if (!this.invoicedSummary) {
        const symbol = this.usSummary ? '$' : '£'
        const sum = this.usSummary ? this.usSummaryOrderSum : this.ukSummaryOrderSum
        return [
          { title: 'Orders Summed Count', lines: [{ value: this.orders.length }] },
          {
            title: 'Orders Summed Value',
            lines: [{ value: symbol + this.$numeral(sum).format('0,0.00'), note: '(inc. tax & shipping)' }]
          }
        ]
      }

      const cards = [
        {
          title: 'Invoices Count',
          lines: [{ value: this.invoices.length }]
        }
      ]

      Object.keys(this.totalsByCurrency)
        .sort()
        .forEach((code) => {
          const t = this.totalsByCurrency[code]
          const symbol = CURRENCY_SYMBOLS[code] || ''
          const fmt = (n) => symbol + this.$numeral(n).format('0,0.00')

          if (t.gross !== 0 || t.net !== 0) {
            cards.push({
              title: 'Invoiced ' + code,
              lines: [
                { label: 'Net (excl. tax)', value: fmt(t.net) },
                { label: 'Tax', value: fmt(t.tax) },
                { label: 'Gross (inc. tax)', value: fmt(t.gross) }
              ]
            })
          }

          if (t.creditCount > 0) {
            cards.push({
              title: 'Credit Notes ' + code,
              lines: [
                { label: 'Count', value: t.creditCount },
                { label: 'Net', value: '-' + fmt(t.creditNet) },
                { label: 'Gross', value: '-' + fmt(t.creditGross) }
              ]
            })
          }
        })

      return cards
    },
    summaryCardCols() {
      return this.summaryCards.length > 2 ? 4 : 6
    }
  },
  methods: {
    async findMatchingQuote(quoteID) {
      try {
        const quoteSnap = await getDoc(doc(db, 'quotes', quoteID))
        if (quoteSnap.exists()) {
          return quoteSnap.data()
        }
        return null
      } catch (error) {
        console.error('Error fetching quote:', error)
        return null
      }
    },
    resetResults() {
      this.usSummaryOrderSum = 0
      this.ukSummaryOrderSum = 0
      this.orders = []
      this.invoices = []
      this.creditNotes = []
      this.orderMetaByNumber = {}
    },
    toggleRegion() {
      this.usSummary = !this.usSummary
      this.resetResults()
      this.loadData()
    },
    toggleMode() {
      this.invoicedSummary = !this.invoicedSummary
      this.resetResults()
      this.loadData()
    },
    dateRangeChanged(range) {
      // If range is empty, null, or invalid, don't load data
      if (!range || !range[0] || !range[1]) {
        this.activeDateRange = null
        this.resetResults()
        return
      }

      this.activeDateRange = range
      this.resetResults()
      this.loadData()
    },
    loadData() {
      return this.invoicedSummary ? this.loadInvoicedData() : this.loadOrdersData()
    },
    async loadOrdersData() {
      // Don't load if no date range is selected
      if (!this.activeDateRange || !this.activeDateRange[0] || !this.activeDateRange[1]) {
        return
      }

      this.loading = true
      try {
        const region = this.usSummary ? 'us' : 'uk'
        const ordersSnap = await getDocs(
          query(
            collection(db, 'orders'),
            where('type', '==', 'mega-admin'),
            where('region', '==', region),
            where('foc', '==', false),
            where('dateCreated', '>=', this.activeDateRange[0].startOf('day').toDate()),
            where('dateCreated', '<=', this.activeDateRange[1].endOf('day').toDate())
          )
        )

        // drop recuts — they're tracked against their original order
        const filteredOrders = ordersSnap.docs.filter((order) => {
          return order.data().recut === undefined
        })

        // Sort filtered orders by orderCreated (newest first)
        filteredOrders.sort((a, b) => {
          const dateA = this.$moment(a.data().dateCreated)
          const dateB = this.$moment(b.data().dateCreated)
          return dateB.diff(dateA) // Newest first (descending order)
        })

        this.orders = []

        // Global tally for front types across all orders
        const globalFrontTypesTally = {
          metod: { orderCount: 0, orderTotal: 0 },
          paxPlatsa: { orderCount: 0, orderTotal: 0 },
          none: { orderCount: 0, orderTotal: 0 }
        }

        // Push clean order data object
        for (const orderDoc of filteredOrders) {
          try {
            const orderData = orderDoc.data()
            // Fetch matching quotes for each order
            const quoteData = await this.findMatchingQuote(orderData.quoteID)

            // look through quoteData.versions and find the versionID that matches orderData.versionID
            if (quoteData && quoteData.versions && Array.isArray(quoteData.versions)) {
              const matchingVersion = quoteData.versions.find((version) => version.id === orderData.versionID)
              if (matchingVersion && matchingVersion.frontTypes) {
                const frontTypesTally = matchingVersion.frontTypes.reduce(
                  (acc, frontType) => {
                    acc[frontType] = {
                      orderCount: (acc[frontType]?.orderCount || 0) + 1,
                      orderTotal: (acc[frontType]?.orderTotal || 0) + orderData.total
                    }
                    return acc
                  },
                  {
                    metod: { orderCount: 0, orderTotal: 0 },
                    paxPlatsa: { orderCount: 0, orderTotal: 0 },
                    none: { orderCount: 0, orderTotal: 0 }
                  }
                )

                // Accumulate into global tally
                Object.keys(frontTypesTally).forEach((frontType) => {
                  if (globalFrontTypesTally[frontType]) {
                    globalFrontTypesTally[frontType].orderCount += frontTypesTally[frontType].orderCount
                    globalFrontTypesTally[frontType].orderTotal += frontTypesTally[frontType].orderTotal
                  }
                })
              }
            }

            // Create order object
            const orderObj = {
              firstName: orderData.customerContactDetails.firstName,
              lastName: orderData.customerContactDetails.lastName,
              name: orderData.customerContactDetails.firstName + ' ' + orderData.customerContactDetails.lastName,
              customerEmail: orderData.customerEmail,
              orderCreated: orderData.dateCreated,
              orderNumber: orderData.orderNumber,
              orderTotal: orderData.total || 0, // incl. vat if UK and sales tax if US
              customerAddress1: orderData.customerContactDetails.deliveryAddressLine1 || '',
              customerAddress2: orderData.customerContactDetails.deliveryAddressLine2 || '',
              customerCity: orderData.customerContactDetails.deliveryCity || '',
              customerPostcode: orderData.customerContactDetails.deliveryPostcode || '',
              customerCountry: orderData.customerContactDetails.deliveryCountry || '',
              customerState: orderData.customerContactDetails.deliveryState || '',
              quoteCreated: quoteData ? quoteData.dateCreated : null
            }
            this.orders.push(orderObj)
          } catch (error) {
            console.error('Error processing order:', error)
          }
        }

        // Log global front types tally
        console.log('Global Front Types Tally:', globalFrontTypesTally)

        // Calculate sums based on current region
        const orderSum = this.orders.reduce((acc, order) => acc + order.orderTotal, 0)

        this.usSummaryOrderSum = this.usSummary ? orderSum : 0
        this.ukSummaryOrderSum = this.usSummary ? 0 : orderSum
      } catch (error) {
        console.error('Error loading orders:', error)
        this.$store.commit('openSnackbar', { snackbarText: 'Could not load orders: ' + error.message })
      } finally {
        this.loading = false
      }
    },
    async loadInvoicedData() {
      // Don't load if no date range is selected
      if (!this.activeDateRange || !this.activeDateRange[0] || !this.activeDateRange[1]) {
        return
      }

      this.loading = true
      try {
        const getInvoices = httpsCallable(functions, 'getXeroInvoicesForPeriod')
        const result = await getInvoices({
          tenant: this.usSummary ? 'us' : 'uk',
          from: this.activeDateRange[0].format('YYYY-MM-DD'),
          to: this.activeDateRange[1].format('YYYY-MM-DD')
        })

        const invoices = (result?.data?.invoices || []).slice()
        this.creditNotes = result?.data?.creditNotes || []

        await this.buildOrderMeta([...invoices, ...this.creditNotes])

        // Newest first, matching the ordered view
        invoices.sort((a, b) => (b.date || '').localeCompare(a.date || ''))

        this.invoices = invoices.map((i) => ({ ...i, ...this.decorateInvoice(i) }))
      } catch (error) {
        console.error('Error loading Xero invoices:', error)
        this.$store.commit('openSnackbar', {
          snackbarText: 'Could not load Xero invoices: ' + (error.message || error)
        })
      } finally {
        this.loading = false
      }
    },
    async buildOrderMeta(documents) {
      const meta = {}
      const values = new Set()

      documents.forEach((d) => {
        const ref = (d.reference || '').trim()
        if (!ref) return
        values.add(ref)
        if (/^\d+$/.test(ref)) values.add(Number(ref))
      })

      const lookups = [...values]

      // Firestore caps `in` queries at 30 values
      for (let i = 0; i < lookups.length; i += 30) {
        const chunk = lookups.slice(i, i + 30)
        try {
          const snap = await getDocs(query(collection(db, 'orders'), where('orderNumber', 'in', chunk)))
          snap.docs.forEach((orderDoc) => {
            const data = orderDoc.data()
            const contact = data.customerContactDetails || {}
            const address = [contact.deliveryAddressLine1, contact.deliveryAddressLine2].filter(Boolean).join(', ')
            // Key as a string so string references match number-typed orderNumbers
            meta[String(data.orderNumber)] = {
              type: data.type || null,
              region: data.region || null,
              customerEmail: data.customerEmail || contact.email || '',
              firstName: contact.firstName || '',
              lastName: contact.lastName || '',
              customerAddress: address,
              customerAddress1: contact.deliveryAddressLine1 || '',
              customerAddress2: contact.deliveryAddressLine2 || '',
              customerCity: contact.deliveryCity || '',
              customerPostcode: contact.deliveryPostcode || '',
              customerCountry: contact.deliveryCountry || '',
              customerState: contact.deliveryState || ''
            }
          })
        } catch (error) {
          console.error('Error joining orders for references', chunk, error)
        }
      }

      this.orderMetaByNumber = meta
    },
    decorateInvoice(invoice) {
      const ref = (invoice.reference || '').trim()
      const meta = ref ? this.orderMetaByNumber[ref] : null
      let source = 'Not in MA'
      if (meta) {
        source = meta.type === 'mega-admin' ? 'Order' : meta.type === 'sample' ? 'Sample' : meta.type || 'Order'
      }
      // Flattened onto the row so the data table can bind the columns directly
      return {
        source,
        meta,
        customerEmail: (meta && meta.customerEmail) || '',
        customerAddress: (meta && meta.customerAddress) || '',
        customerCity: (meta && meta.customerCity) || '',
        customerState: (meta && meta.customerState) || '',
        customerPostcode: (meta && meta.customerPostcode) || '',
        customerCountry: (meta && meta.customerCountry) || ''
      }
    },
    downloadCSV() {
      const csvContent = this.invoicedSummary ? this.buildInvoicedCSV() : this.buildOrderedCSV()

      // Create filename
      const region = this.usSummary ? 'US' : this.invoicedSummary ? 'UK+EU' : 'UK'
      const label = this.invoicedSummary ? 'Invoiced Summary' : 'Order Summary'
      let filename
      if (this.activeDateRange && this.activeDateRange[0] && this.activeDateRange[1]) {
        const startDate = this.$moment(this.activeDateRange[0]).format('DD MMM YYYY')
        const endDate = this.$moment(this.activeDateRange[1]).format('DD MMM YYYY')
        filename = `${region} ${label} - ${startDate} to ${endDate}.csv`
      } else {
        filename = `${region} ${label}.csv`
      }

      // Create and download blob
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', filename)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    buildOrderedCSV() {
      // CSV headers
      const headers = [
        'firstName',
        'lastName',
        'customerEmail',
        'orderNumber',
        'orderCreated',
        'quoteCreated',
        'orderTotal',
        'address1',
        'address2',
        'city',
        'country',
        'state',
        'postcode'
      ]

      // Convert orders to CSV format (already sorted by orderCreated)
      return [
        headers.join(','),
        ...this.orders.map((order) =>
          [
            this.escapeCSV(order.firstName || ''),
            this.escapeCSV(order.lastName || ''),
            this.escapeCSV(order.customerEmail || ''),
            this.escapeCSV(order.orderNumber || ''),
            this.escapeCSV(this.formatDateForCSV(order.orderCreated)),
            this.escapeCSV(this.formatDateForCSV(order.quoteCreated)),
            order.orderTotal || 0,
            this.escapeCSV(order.customerAddress1 || ''),
            this.escapeCSV(order.customerAddress2 || ''),
            this.escapeCSV(order.customerCity || ''),
            this.escapeCSV(order.customerCountry || ''),
            this.escapeCSV(order.customerState || ''),
            this.escapeCSV(order.customerPostcode || '')
          ].join(',')
        )
      ].join('\n')
    },
    buildInvoicedCSV() {
      const headers = [
        'documentType',
        'invoiceNumber',
        'orderNumber',
        'contactName',
        'invoiceDate',
        'source',
        'currency',
        'net',
        'tax',
        'total',
        'status',
        'customerEmail',
        'address1',
        'address2',
        'city',
        'country',
        'state',
        'postcode'
      ]

      const row = (docType, d, source, meta, number, sign) => {
        const m = meta || {}
        return [
          docType,
          this.escapeCSV(number || ''),
          this.escapeCSV(d.reference || ''),
          this.escapeCSV(d.contactName || ''),
          this.escapeCSV(d.date || ''),
          this.escapeCSV(source),
          this.escapeCSV(d.currencyCode || ''),
          sign * d.subTotal,
          sign * d.totalTax,
          sign * d.total,
          this.escapeCSV(d.status || ''),
          this.escapeCSV(m.customerEmail || ''),
          this.escapeCSV(m.customerAddress1 || ''),
          this.escapeCSV(m.customerAddress2 || ''),
          this.escapeCSV(m.customerCity || ''),
          this.escapeCSV(m.customerCountry || ''),
          this.escapeCSV(m.customerState || ''),
          this.escapeCSV(m.customerPostcode || '')
        ].join(',')
      }

      return [
        headers.join(','),
        ...this.invoices.map((i) => row('Invoice', i, i.source, i.meta, i.invoiceNumber, 1)),
        // Credit notes are exported negative so the column totals net out
        ...this.creditNotes.map((c) => {
          const { source, meta } = this.decorateInvoice(c)
          return row('Credit Note', c, source, meta, c.creditNoteNumber, -1)
        })
      ].join('\n')
    },
    escapeCSV(value) {
      // Escape CSV values that contain commas, quotes, or newlines
      if (value === null || value === undefined) return ''
      const stringValue = String(value)
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`
      }
      return stringValue
    },
    formatDateForCSV(date) {
      if (!date) return ''

      // Handle Firestore Timestamp objects
      let jsDate
      if (date && typeof date.toDate === 'function') {
        // It's a Firestore Timestamp
        jsDate = date.toDate()
      } else if (date instanceof Date) {
        // It's already a JavaScript Date
        jsDate = date
      } else {
        // Try to parse it as a date
        jsDate = new Date(date)
      }

      // Check if the date is valid
      if (isNaN(jsDate.getTime())) {
        return 'Invalid Date'
      }

      return this.$moment(jsDate).format('DD/MM/YYYY')
    }
  }
}
</script>
